/**
 * FoodPulse — "Your businesses" strip.
 *
 * Renders a subtle rail near the top of the Food Pulse home for
 * anyone who has claimed one or more listings on this device.
 * We store a lightweight owner marker in localStorage
 * (`fp_owner_businesses`) after each successful OTP verification —
 * this hook simply reads it and gives a one-tap link back into the
 * owner dashboard (SocialPulse's `/business/:id/edit`).
 *
 * Not-signed-in users are the primary audience — signed-in owners
 * already have "My businesses" in the account menu.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store } from "lucide-react";

export default function OwnerBusinessesStrip() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("fp_owner_businesses");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        // Most recently claimed first.
        setItems(parsed.slice().reverse().slice(0, 3));
      }
    } catch { /* corrupt storage — ignore */ }
  }, []);

  if (!items.length) return null;

  return (
    <div className="border-b" style={{ background: "#F0F7EA", borderColor: "#B8D3A1" }} data-testid="fp-owner-strip">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm flex-wrap">
        <Store className="w-4 h-4 shrink-0" style={{ color: "#2F6B2F" }} />
        <span className="font-mono text-[11px] uppercase tracking-widest opacity-80" style={{ color: "#2F6B2F" }}>
          You manage
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {items.map((b) => (
            <Link key={b.business_id || b.food_slug}
                  to={b.manage_url || `/business/${b.business_id}/edit`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white hover:brightness-95 border"
                  style={{ borderColor: "#B8D3A1", color: "#1A3D1A" }}
                  data-testid={`owner-strip-link-${b.business_id || b.food_slug}`}>
                <span className="max-w-[220px] truncate">{b.name || b.business_slug}</span>
                <span className="opacity-60">→</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
